import { UserManager, WebStorageStateStore } from "oidc-client-ts";

// read from the Vite‐provided env, with fallback defaults for development
const AUTHORITY = import.meta.env.VITE_AUTHORITY || "https://localhost:7188";
const CLIENT_ID = import.meta.env.VITE_CLIENT_ID || "vue_spa";
const SCOPE = import.meta.env.VITE_SCOPE || "openid profile email api offline_access";

// compute your callback URLs based on the current origin
const ORIGIN = window.location.origin;
const REDIRECT = `${ORIGIN}/callback`;
const LOGOUT = `${ORIGIN}/signout-callback`;

export const oidcConfig = {
  authority: AUTHORITY,
  client_id: CLIENT_ID,
  redirect_uri: REDIRECT,
  response_type: "code",
  scope: SCOPE,
  post_logout_redirect_uri: LOGOUT,

  // keep your silent renew settings:
  automaticSilentRenew: true,
  includeIdTokenInSilentRenew: true,

  // Persist the OIDC user (access token + refresh token) in localStorage
  // instead of the oidc-client-ts default of sessionStorage. Without this,
  // closing the browser wipes the user object even when the Identity cookie
  // and access token are still valid — forcing a redirect through
  // /connect/authorize on every fresh tab. Mobile already does this; the
  // Trust Device 90-day token is wasted otherwise.
  userStore: new WebStorageStateStore({ store: window.localStorage }),

  // Make sure redirect_uri matches exactly what's registered on the server
  loadUserInfo: false, // Don't load user info endpoint (not needed for code flow)
};

// ✅ Only ONE instance created here
export const mgr = new UserManager(oidcConfig);

// Note: localStorage.claims is written by stores/auth.js via /api/AccessRights,
// which is always fresh. Previously also written here from id_token.perm at
// login, but that copy went stale on perm changes mid-session and could race
// with the API write.

// ✅ Export the same instance under the old name for compatibility
export const userManager = mgr;

async function failClosed(reason, err) {
  if (err) console.error(`[OIDC] ${reason}:`, err);
  try {
    const { useAuthStore } = await import("@/stores/auth");
    const returnPath = window.location.pathname + window.location.search;
    await useAuthStore().recoverToLogin(reason, returnPath);
  } catch (recoveryError) {
    console.error("[OIDC] Unable to restart login:", recoveryError);
  }
}

// automaticSilentRenew owns the renewal attempt. We only handle its terminal
// failure and a server-side sign-out, avoiding duplicate simultaneous renewals.
userManager.events.addSilentRenewError((err) => failClosed("renew_failed", err));
userManager.events.addUserSignedOut(() => failClosed("remote_signout"));
